import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { PlaybackState } from '../components/Player/PlaybackState';

const { IDLE, BUFFERING, PAUSED } = PlaybackState;

export type PlaybackInformationProps = {
	player: any;
	onCloseRequested: () => void;
	onShowPlaybackControlsRequested: () => void;
	position: number;
	duration: number;
	playbackState: PlaybackState;
	visible: boolean;
	date: Date;
	children?: React.ReactElement;
};

type PlaybackInformationContextProps = PlaybackInformationProps & {
	setPosition: (position: number) => void;
	playbackInformationVisible: boolean;
	positionToSeekTo: number;
	isLive: boolean;
	isCasting: boolean;
};

export const PlaybackInformationContext = createContext<Partial<PlaybackInformationContextProps>>({});

export const PlaybackInformationContextProvider: React.FC<PlaybackInformationProps> = (props) => {
	const { children, player, onCloseRequested, onShowPlaybackControlsRequested, position, duration, playbackState, visible, date } = props;

	const [positionToSeekTo, setPositionToSeekTo] = useState<number>(null);

	const setPosition = useCallback(
		(positionToSet: number) => {
			player.seekToPosition(positionToSet);

			setPositionToSeekTo(null);
		},
		[player]
	);

	const seekToOffset = useCallback(
		(offset) => {
			const base = positionToSeekTo || position;

			const newPositionToSeekTo = Math.min(Math.max(0, base + offset), duration);

			setPositionToSeekTo(newPositionToSeekTo);
		},
		[duration, position, positionToSeekTo]
	);

	useDebounce(
		() => {
			if (positionToSeekTo) {
				setPosition(positionToSeekTo);
			}
		},
		200,
		[positionToSeekTo]
	);

	useEffect(() => {
		const onKeypress = (event) => {
			if (!player) return;

			if (!player?.isAdPlayback()) {
				onShowPlaybackControlsRequested();

				switch (event.key) {
					case 'Enter':
					case ' ': // SPACE
					case 'MediaPlayPause':
						player.togglePausePlay();
						break;
					case 'ArrowLeft':
					case 'MediaRewind':
						seekToOffset(-10);
						break;
					case 'ArrowRight':
					case 'MediaFastForward':
						seekToOffset(10);
						break;
					case 'MediaPlay':
						player.play();
						break;
					case 'MediaPause':
						player.pause();
						break;
					case 'ArrowUp':
						player.increaseVolume();
						break;
					case 'ArrowDown':
						player.decreaseVolume();
						break;
					case 'm':
						player.toggleMuted();
						break;
					case 'f':
						player.toggleFullscreen();
						break;
					case 'p':
						player.togglePIP();
						break;
					default:
				}
			} else {
				switch (event.key) {
					case 's':
						player.skipAd();
						break;
					default:
				}
			}
		};

		document.addEventListener('keydown', onKeypress);

		return () => {
			document.removeEventListener('keydown', onKeypress);
		};
	}, [onShowPlaybackControlsRequested, player, seekToOffset]);

	const playbackInformationVisible = useMemo(() => {
		return (player && player.isCasting()) || visible || [IDLE, BUFFERING, PAUSED].includes(playbackState);
	}, [playbackState, player, visible]);

	const dateToRender = useMemo(() => {
		if (!player?.isLive()) return null;

		if (!date) return date;

		if (positionToSeekTo) {
			return new Date((positionToSeekTo - position) * 1000 + date.getTime());
		}

		return date;
	}, [date, player, position, positionToSeekTo]);

	const value = useMemo(() => {
		return {
			player,
			onShowPlaybackControlsRequested,
			onCloseRequested,
			position,
			setPosition,
			duration,
			playbackState,
			visible,
			isLive: player?.isLive() ?? false,
			isCasting: player?.isCasting() ?? false,
			date: dateToRender,
			playbackInformationVisible,
		};
	}, [dateToRender, duration, onCloseRequested, onShowPlaybackControlsRequested, playbackInformationVisible, playbackState, player, position, setPosition, visible]);

	return <PlaybackInformationContext.Provider value={value}>{children}</PlaybackInformationContext.Provider>;
};
